import { useMemo, useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { propertiesData } from '../data/propertiesData';
import { motion } from 'framer-motion';

const locations = [
  { name: "Johannesburg", image: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600&h=400&fit=crop" },
  { name: "Benoni", image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop" },
  { name: "Pretoria", image: "https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=600&h=400&fit=crop" },
  { name: "Witbank", image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop" },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};


export default function LocationSection() {
  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();
  
  // Tracks if the user has ever interacted (permanently stops auto-scroll)
  const [hasInteracted, setHasInteracted] = useState(false);
  // Tracks if the user is currently interacting (stops scroll while touch/hover is active)
  const [isInteracting, setIsInteracting] = useState(false); 

  // --- Auto-Scroll Logic: VERY SLOW Speed & Stops permanently after first interaction ---
  useEffect(() => {
    const scrollElement = scrollContainerRef.current;
    if (!scrollElement) return;

    // ⬇️ DECREASED: Scroll by only 0.2 pixels per step for an ultra-slow crawl
    const scrollIncrement = 0.2; 
    // ⬆️ INCREASED: Wait 30ms between steps
    const intervalTime = 30; 

    let scrollIntervalId = null;

    const startScrolling = () => {
        // ONLY start if: 1. No scroll is running AND 2. The user has NOT interacted yet
        if (scrollIntervalId || hasInteracted || isInteracting) return;

        scrollIntervalId = setInterval(() => {
            // Check if scroll position is at the very end
            if (scrollElement.scrollLeft + scrollElement.clientWidth >= scrollElement.scrollWidth) {
                // Instantly jump to the beginning for a seamless loop
                scrollElement.scrollLeft = 0; 
            } else {
                // Scroll continuously by the small increment
                scrollElement.scrollLeft += scrollIncrement;
            }
        }, intervalTime);
    };

    const stopScrolling = () => {
        if (scrollIntervalId) {
            clearInterval(scrollIntervalId);
            scrollIntervalId = null;
        }
    };

    // Control the scrolling: Stop if the user has interacted at all
    if (hasInteracted || isInteracting) {
        stopScrolling();
    } else {
        startScrolling();
    }

    // Cleanup function
    return () => stopScrolling();
  }, [hasInteracted, isInteracting]); 

  // Handler to permanently stop the scroll on any user interaction
  const handleInteractionStart = () => {
    // 1. Set the flag to permanently stop the automatic scroll
    setHasInteracted(true);
    // 2. Set the flag to stop the scroll while the user is actively touching/hovering
    setIsInteracting(true); 
  };
  
  // Handler to allow the user to manually move the scroll even after stopping
  const handleInteractionEnd = () => {
    setIsInteracting(false);
  };
  // -------------------------------------------------------------------

  // Duplicating locations to create a seamless infinite loop effect
  const locationsWithStats = useMemo(() => {
    const locationCounts = propertiesData.reduce((acc, property) => {
      const { location, status } = property;
      if (!acc[location]) {
        acc[location] = { total: 0, forRent: 0, forSale: 0 };
      }
      acc[location].total++;
      if (status === 'Rent') acc[location].forRent++;
      else if (status === 'Sale') acc[location].forSale++;
      return acc;
    }, {});

    // Repeat the locations three times to ensure enough content for a loop jump
    const repeatedLocations = [...locations, ...locations, ...locations];

    return repeatedLocations.map((loc, index) => ({
      ...loc,
      name: `${loc.name}-${index}`, 
      originalName: loc.name,
      stats: locationCounts[loc.name] || { total: 0, forRent: 0, forSale: 0 },
    }));
  }, []);

  const handleViewProperties = (locationName) => {
    navigate('/properties', { state: { selectedLocation: locationName } });
  };

  const handleViewDetails = (locationName) => {
    navigate('/view-details', { state: { selectedLocation: locationName } });
  };

  const handleApplyNow = (locationName) => {
    navigate('/apply', { state: { selectedLocation: locationName } });
  };

  return (
    <section id="properties" className="bg-gray-100 py-12 md:py-20 text-center">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="text-xs sm:text-sm font-semibold text-orange-500 uppercase tracking-wider">
          EXPLORE CITIES
        </span>
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight mt-2 text-black">
          WHERE WE OPERATE
        </h2>
        <p className="text-gray-600 max-w-3xl mx-auto mt-3 sm:mt-4 text-base sm:text-lg px-4">
          Explore available properties across our key locations in South Africa.
        </p>

        <div className="mt-8 sm:mt-12">
          <motion.div
            ref={scrollContainerRef}
            className="flex gap-4 sm:gap-6 overflow-x-auto pb-4 scrollbar-hide cursor-grab"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            // Interaction handlers to permanently stop the scroll
            onMouseEnter={handleInteractionStart} 
            onMouseLeave={handleInteractionEnd}   
            onTouchStart={handleInteractionStart} 
            onTouchEnd={handleInteractionEnd}     
          >
            {locationsWithStats.map((loc) => (
              <motion.div
                key={loc.name}
                className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden flex-shrink-0 w-64 sm:w-72 hover:shadow-xl transition-shadow duration-300"
                variants={cardVariants}
              >
                <div className="h-40 sm:h-48 overflow-hidden">
                  <img src={loc.image} alt={`Properties in ${loc.originalName}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-4 sm:p-5">
                  <h3 className="text-xl sm:text-2xl font-bold text-black mb-1 sm:mb-2">{loc.originalName}</h3>
                  {loc.stats.total > 0 ? (
                    <div className="mb-4">
                      <p className="text-orange-500 font-semibold text-base">
                        {loc.stats.total} {loc.stats.total === 1 ? 'Property' : 'Properties'}
                      </p>
                      <p className="text-gray-600 text-xs mt-1">
                        {loc.stats.forRent > 0 && `${loc.stats.forRent} for rent`}
                        {loc.stats.forRent > 0 && loc.stats.forSale > 0 && ' • '}
                        {loc.stats.forSale > 0 && `${loc.stats.forSale} for sale`}
                      </p>
                    </div>
                  ) : (
                    <p className="text-gray-500 mb-4 text-sm">Coming soon</p>
                  )}

                  {loc.stats.total > 0 ? (
                    <div>
                      <div className="flex gap-2 sm:gap-3">
                        <button
                          onClick={() => handleViewProperties(loc.originalName)}
                          className="hidden sm:flex-1 px-4 py-2 rounded-full border-2 border-orange-500 text-orange-500 bg-white font-semibold text-sm hover:bg-orange-500 hover:text-white transition-all"
                        >
                          View More
                        </button>
                        <button
                          onClick={() => handleApplyNow(loc.originalName)}
                          className="flex-1 px-4 py-2 rounded-full bg-black text-white font-semibold text-sm hover:bg-gray-800 transition-colors"
                        >
                          Apply Now
                        </button>
                      </div>
                      <button
                        onClick={() => handleViewDetails(loc.originalName)}
                        className="mt-3 text-sm text-gray-500 hover:text-black hover:underline transition-colors"
                      >
                        View Details
                      </button>
                    </div>
                  ) : (
                    <button disabled className="w-full px-4 py-2 rounded-full bg-gray-300 text-gray-500 font-semibold text-sm cursor-not-allowed">
                      No Properties Available
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
      <style jsx global>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
}